import React, { useState, useEffect } from "react";
import {
  FeedbackAnalytics as FeedbackAnalyticsType,
  FeedbackStats,
} from "../types";
import { apiService } from "../services/api";
import {
  TrendingUp,
  TrendingDown,
  Users,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  BarChart3,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";

interface FeedbackAnalyticsProps {
  className?: string;
}

const FeedbackAnalytics: React.FC<FeedbackAnalyticsProps> = ({
  className = "",
}) => {
  const [analytics, setAnalytics] = useState<FeedbackAnalyticsType | null>(
    null
  );
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [daysBack, setDaysBack] = useState(30);
  const [refreshing, setRefreshing] = useState(false);

  const loadAnalytics = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [analyticsData, statsData] = await Promise.all([
        apiService.getFeedbackAnalytics(daysBack),
        apiService.getFeedbackStats(),
      ]);

      setAnalytics(analyticsData);
      setStats(statsData.stats);
    } catch (error) {
      console.error("Error loading feedback analytics:", error);
      setError(
        "Failed to load feedback analytics. You may not have admin privileges."
      );
    } finally {
      setLoading(false);
    }
  }, [daysBack]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAnalytics();
    setRefreshing(false);
  };

  if (loading) {
    return (
      <div
        className={`bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-6 ${className}`}
      >
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-neutral-600 dark:text-neutral-300">
              Loading analytics...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-6 ${className}`}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
              Access Restricted
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400 mb-4">
              {error}
            </p>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!analytics || !stats) {
    return null;
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
            Feedback Analytics
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 mt-1">
            Monitor AI performance and user satisfaction
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Time Period Selector */}
          <select
            value={daysBack}
            onChange={(e) => setDaysBack(Number(e.target.value))}
            className="px-3 py-2 bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>

          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors"
            title="Refresh data"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Conversations */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                Total Conversations
              </p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                {analytics.total_conversations.toLocaleString()}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        {/* Total Messages */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                Total Messages
              </p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                {analytics.total_messages.toLocaleString()}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>

        {/* Feedback Rate */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                Feedback Rate
              </p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                {analytics.feedback_rate.toFixed(1)}%
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
        </div>

        {/* Satisfaction Score */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                Satisfaction Score
              </p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                {analytics.satisfaction_score.toFixed(1)}%
              </p>
            </div>
            <div
              className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                analytics.satisfaction_score >= 70
                  ? "bg-green-100 dark:bg-green-900/30"
                  : analytics.satisfaction_score >= 50
                  ? "bg-yellow-100 dark:bg-yellow-900/30"
                  : "bg-red-100 dark:bg-red-900/30"
              }`}
            >
              {analytics.satisfaction_score >= 70 ? (
                <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
              ) : (
                <TrendingDown className="w-6 h-6 text-red-600 dark:text-red-400" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Thumbs Up/Down Breakdown */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            Feedback Breakdown
          </h3>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <ThumbsUp className="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <span className="text-neutral-700 dark:text-neutral-300">
                  Positive
                </span>
              </div>
              <div className="text-right">
                <div className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                  {stats.thumbs_up_count.toLocaleString()}
                </div>
                <div className="text-sm text-green-600 dark:text-green-400">
                  {stats.thumbs_up_percentage.toFixed(1)}%
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                  <ThumbsDown className="w-4 h-4 text-red-600 dark:text-red-400" />
                </div>
                <span className="text-neutral-700 dark:text-neutral-300">
                  Negative
                </span>
              </div>
              <div className="text-right">
                <div className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                  {stats.thumbs_down_count.toLocaleString()}
                </div>
                <div className="text-sm text-red-600 dark:text-red-400">
                  {stats.thumbs_down_percentage.toFixed(1)}%
                </div>
              </div>
            </div>

            {/* Visual Progress Bar */}
            <div className="mt-4">
              <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-3">
                <div
                  className="bg-green-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${stats.thumbs_up_percentage}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Confidence vs Satisfaction */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            Confidence vs Satisfaction
          </h3>

          <div className="space-y-3">
            {Object.entries(analytics.confidence_vs_satisfaction).map(
              ([range, satisfaction]) => (
                <div key={range} className="flex items-center justify-between">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                    {range}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                      {satisfaction.toFixed(1)}%
                    </span>
                    <div className="w-16 bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
                      <div
                        className="bg-orange-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${satisfaction}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {/* Daily Trends */}
      {analytics.daily_feedback_trend.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            Daily Feedback Trends
          </h3>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200 dark:border-neutral-700">
                  <th className="text-left py-2 text-neutral-600 dark:text-neutral-400">
                    Date
                  </th>
                  <th className="text-right py-2 text-neutral-600 dark:text-neutral-400">
                    Total
                  </th>
                  <th className="text-right py-2 text-neutral-600 dark:text-neutral-400">
                    Positive
                  </th>
                  <th className="text-right py-2 text-neutral-600 dark:text-neutral-400">
                    Negative
                  </th>
                  <th className="text-right py-2 text-neutral-600 dark:text-neutral-400">
                    Satisfaction
                  </th>
                </tr>
              </thead>
              <tbody>
                {analytics.daily_feedback_trend.slice(-7).map((day) => (
                  <tr
                    key={day.date}
                    className="border-b border-neutral-100 dark:border-neutral-700/50"
                  >
                    <td className="py-2 text-neutral-900 dark:text-neutral-100">
                      {new Date(day.date).toLocaleDateString()}
                    </td>
                    <td className="text-right py-2 text-neutral-700 dark:text-neutral-300">
                      {day.total_feedback}
                    </td>
                    <td className="text-right py-2 text-green-600 dark:text-green-400">
                      {day.positive_feedback}
                    </td>
                    <td className="text-right py-2 text-red-600 dark:text-red-400">
                      {day.negative_feedback}
                    </td>
                    <td className="text-right py-2 text-neutral-900 dark:text-neutral-100 font-medium">
                      {day.satisfaction_rate.toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Improvement Opportunities */}
      {analytics.improvement_opportunities.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            Improvement Opportunities
          </h3>

          <div className="space-y-2">
            {analytics.improvement_opportunities.map((suggestion, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg"
              >
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-neutral-700 dark:text-neutral-300">
                  {suggestion}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackAnalytics;
